import { AllowedParam, CookiesArray } from 'models/cookies'
import { getCurrentLanguage } from 'utils/current-language'
import { SupportedLanguages } from 'utils/languages'

export const isPanelUrl = (url: URL): boolean =>
  url.hostname.includes('klient.') || url.hostname.includes('panel.') || url.pathname.startsWith('/logowanie')

export const doesUrlContainParam = (key: string, urlParamsObject?: object): boolean => {
  urlParamsObject = urlParamsObject || getParamsFromUrl()
  return !!urlParamsObject[key]
}

export const getParamsFromUrl = (): object => {
  const urlParams = new URLSearchParams(window.location.search)
  return Array.from(urlParams.keys()).length > 0 ? Object.fromEntries(urlParams.entries()) : {}
}

export const getSingleParamFromUrl = (key: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.has(key) ? urlParams.get(key) : null
}

export const removeUrlParams = (url: string, paramsToRemove: string[]): string => {
  const urlObj = new URL(url)
  const searchParams = urlObj.searchParams

  paramsToRemove.forEach((param) => searchParams.delete(param))

  return urlObj.origin + urlObj.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '') + urlObj.hash
}

export function renameQueryParam(fromParam: string, toParam: string): void {
  const currentUrl = new URL(window.location.href)
  const params = currentUrl.searchParams

  if (params.has(fromParam)) {
    const value = params.get(fromParam)
    params.delete(fromParam)
    params.set(toParam, value || '')

    const newUrl = `${currentUrl.pathname}?${params.toString()}${currentUrl.hash}`
    window.history.replaceState({}, '', newUrl)
  }
}

export const decodeUrlParams = (url: string): Record<string, any> => {
  try {
    const { search, hash } = new URL(url)
    const searchParams = Object.fromEntries(new URLSearchParams(search).entries())
    const paramsString = hash.startsWith('#/?') ? hash.slice(2) : ''
    const encodedParams = paramsString ? new URLSearchParams(paramsString).get('data') : null
    let decodedParams = {}

    if (encodedParams) {
      decodedParams = JSON.parse(decodeURIComponent(encodedParams))
      decodedParams = Array.isArray(decodedParams) ? Object.assign({}, ...decodedParams) : decodedParams
    }
    return { ...searchParams, ...decodedParams }
  } catch (error) {
    console.error('Error decoding URL params:', error)
    return {}
  }
}

export const createUrlWithParams = (cookiesToParam: CookiesArray, url: URL): string => {
  const newUrl = new URL(url)
  const cookieParams = new URLSearchParams(Object.entries(cookiesToParam).map(([key, value]) => [key, String(value)]))

  if (!isPanelUrl(newUrl)) {
    newUrl.search = cookieParams.toString()
    return newUrl.toString()
  }

  newUrl.searchParams.delete('lang')

  if (cookieParams.has('language')) {
    newUrl.searchParams.set('lang', cookieParams.get('language')!)
    cookieParams.delete('language')
  } else {
    newUrl.searchParams.set('lang', getCurrentLanguage())
  }

  const remainingParams = cookieParams.toString()
  if (remainingParams) {
    const hash = newUrl.hash || '#/'

    const separator = hash.includes('?') ? '&' : '?'
    newUrl.hash = `${hash}${separator}${remainingParams}`
  } else if (!newUrl.hash) {
    newUrl.hash = '#/'
  }

  return newUrl.toString()
}

export function validateParam(param: AllowedParam, value: string): boolean {
  if (param.type === 'boolean') {
    try {
      return JSON.parse(value) === true || JSON.parse(value) === false
    } catch (e) {
      return false
    }
  }
  if (param.type === 'string') {
    if (typeof value !== 'string' || value === 'true' || value === 'false') {
      return false
    }

    if (param.name === 'lang' && param.allowedValues) {
      const allowedLanguages = Object.values(SupportedLanguages)
      return allowedLanguages.includes(value as SupportedLanguages)
    }

    return true
  }
  return false
}
